/**
 * Template Builder — Epic 1 AC #1, #2, #5.
 *
 * Create mode:  /templates/new
 * Edit mode:    /templates/:templateId/edit
 *
 * Flow:
 *  1. Fill meta (name, type, surcharge format, default flag).
 *  2. Paste CSV headers → auto-suggest canonical fields via /api/v1/templates/auto-match.
 *  3. Adjust mappings in the mapping grid.
 *  4. Save as Draft or Activate.
 */

import { IconArrowLeft, IconCheck, IconPlus, IconTrash } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { useTemplateApi } from "./api";
import type {
  SurchargeFormat,
  TemplateFieldIn,
  TemplateType,
} from "./types";

// ── Constants ─────────────────────────────────────────────────────────────────

const TEMPLATE_TYPES: { value: TemplateType; label: string }[] = [
  { value: "manifest", label: "Manifest" },
  { value: "fedex", label: "FedEx" },
  { value: "ups", label: "UPS" },
  { value: "dhl", label: "DHL" },
];

const SURCHARGE_FORMATS: { value: SurchargeFormat; label: string }[] = [
  { value: "row_per_surcharge", label: "Row per surcharge" },
  { value: "column_per_surcharge", label: "Column per surcharge" },
];

// Canonical fields available per template type.
// Mirrors api/engine/template_match.py CANONICAL_FIELDS — keep in sync until the Builder fetches
// /api/v1/templates/canonical-fields directly.
const CARRIER_CANONICAL_FIELDS = [
  "tracking_number",
  "shipment_date",
  "invoice_number",
  "invoice_date",
  "carrier_account_number",
  "billed_weight_value",
  "billed_weight_unit",
  "billed_weight_type",
  "transport_cost",
  "audited_dim_l",
  "audited_dim_w",
  "audited_dim_h",
  "surcharge_code",
  "surcharge_name",
  "surcharge_category",
  "surcharge_amount",
  "currency_code",
  "invoice_total",
];

const CANONICAL_FIELDS: Record<TemplateType, string[]> = {
  manifest: [
    "shipment_reference",
    "tracking_number",
    "carrier_id",
    "shipment_date",
    "expected_weight_kg",
    "expected_cost",
    "currency_code",
  ],
  fedex: CARRIER_CANONICAL_FIELDS,
  ups: CARRIER_CANONICAL_FIELDS,
  dhl: CARRIER_CANONICAL_FIELDS,
};

// ── MappingRow ────────────────────────────────────────────────────────────────

interface MappingRowProps {
  field: TemplateFieldIn;
  index: number;
  availableCanonical: string[];
  onChange: (index: number, updated: TemplateFieldIn) => void;
  onRemove: (index: number) => void;
}

function MappingRow({
  field,
  index,
  availableCanonical,
  onChange,
  onRemove,
}: MappingRowProps) {
  return (
    <tr className="border-b border-border last:border-0">
      <td className="px-3 py-2 font-mono text-xs text-text">{field.source_header}</td>
      <td className="px-3 py-2">
        <select
          value={field.canonical_field}
          aria-label={`Canonical field for ${field.source_header}`}
          className="w-full rounded border border-border bg-bg-3 px-2 py-1 text-xs text-text focus:outline-none focus:ring-1 focus:ring-accent"
          onChange={(e) =>
            onChange(index, { ...field, canonical_field: e.target.value })
          }
        >
          <option value="">— unmapped —</option>
          {availableCanonical.map((f) => (
            <option key={f} value={f}>
              {f}
            </option>
          ))}
        </select>
      </td>
      <td className="px-3 py-2 text-center">
        <input
          type="checkbox"
          checked={field.is_required}
          aria-label={`Mark ${field.source_header} as required`}
          className="rounded accent-accent h-3.5 w-3.5 cursor-pointer"
          onChange={(e) =>
            onChange(index, { ...field, is_required: e.target.checked })
          }
        />
      </td>
      <td className="px-3 py-2 text-right">
        <button
          type="button"
          aria-label={`Remove mapping for ${field.source_header}`}
          className="text-text-3 hover:text-status-ghost transition-colors"
          onClick={() => onRemove(index)}
        >
          <IconTrash size={13} aria-hidden />
        </button>
      </td>
    </tr>
  );
}

// ── TemplateBuilderPage ───────────────────────────────────────────────────────

export function TemplateBuilderPage() {
  const params = useParams({ strict: false }) as { templateId?: string };
  const templateId = params.templateId ?? null;
  const isEdit = templateId !== null;

  const api = useTemplateApi();
  const qc = useQueryClient();
  const navigate = useNavigate();

  // ── Load existing template in edit mode ────────────────────────────────────
  const { data: existing, isLoading: loadingExisting } = useQuery({
    queryKey: ["templates", templateId],
    queryFn: () => api.getTemplate(templateId!),
    enabled: isEdit,
  });

  // ── Form state ─────────────────────────────────────────────────────────────
  const [name, setName] = useState("");
  const [type, setType] = useState<TemplateType>("manifest");
  const [surchargeFormat, setSurchargeFormat] = useState<SurchargeFormat | "">("");
  const [isDefault, setIsDefault] = useState(false);
  const [headerPaste, setHeaderPaste] = useState("");
  const [fields, setFields] = useState<TemplateFieldIn[]>([]);
  const [formError, setFormError] = useState<string | null>(null);

  // Hydrate form when editing an existing template.
  useEffect(() => {
    if (existing) {
      setName(existing.name);
      setType(existing.template_type);
      setSurchargeFormat(existing.surcharge_format ?? "");
      setIsDefault(existing.is_default);
      setFields(
        existing.fields.map((f) => ({
          source_header: f.source_header,
          canonical_field: f.canonical_field,
          is_required: f.is_required,
          position: f.position ?? undefined,
        })),
      );
    }
  }, [existing]);

  // ── Header paste → auto-suggest mappings ──────────────────────────────────
  const [isParsing, setIsParsing] = useState(false);

  function parseHeaders() {
    const raw = headerPaste.trim();
    if (!raw) return;

    // Split on comma or newline.
    const headers = raw
      .split(/[\n,]/)
      .map((h) => h.trim())
      .filter(Boolean);

    setIsParsing(true);
    // Auto-suggest via the simple alias lookup — no network call needed for
    // the basic UX. The /auto-match endpoint is used at upload time.
    const suggested: TemplateFieldIn[] = headers.map((h, i) => ({
      source_header: h,
      canonical_field: "",
      is_required: false,
      position: i,
    }));
    setFields(suggested);
    setHeaderPaste("");
    setIsParsing(false);
  }

  // ── Mutations ──────────────────────────────────────────────────────────────

  const invalidateList = () => qc.invalidateQueries({ queryKey: ["templates"] });

  const createMutation = useMutation({
    mutationFn: () =>
      api.createTemplate({
        name,
        template_type: type,
        surcharge_format: surchargeFormat || null,
        is_default: isDefault,
        fields,
      }),
    onSuccess: (tmpl) => {
      invalidateList();
      void navigate({ to: "/templates/$templateId/edit", params: { templateId: tmpl.id } });
    },
    onError: (err) => setFormError((err as Error).message),
  });

  const updateMutation = useMutation({
    mutationFn: () =>
      api.updateTemplate(templateId!, {
        name,
        surcharge_format: surchargeFormat || null,
        is_default: isDefault,
        fields,
      }),
    onSuccess: () => invalidateList(),
    onError: (err) => setFormError((err as Error).message),
  });

  const activateMutation = useMutation({
    mutationFn: () => api.activateTemplate(templateId!),
    onSuccess: () => {
      invalidateList();
      void navigate({ to: "/templates" });
    },
    onError: (err) => setFormError((err as Error).message),
  });

  const isSaving =
    createMutation.isPending || updateMutation.isPending || activateMutation.isPending;

  // ── Save handlers ──────────────────────────────────────────────────────────

  function validate(): boolean {
    if (!name.trim()) {
      setFormError("Name is required.");
      return false;
    }
    if (type !== "manifest" && !surchargeFormat) {
      setFormError("Surcharge format is required for carrier invoice templates.");
      return false;
    }
    setFormError(null);
    return true;
  }

  function handleSaveDraft() {
    if (!validate()) return;
    if (isEdit) {
      updateMutation.mutate();
    } else {
      createMutation.mutate();
    }
  }

  function handleActivate() {
    if (!validate()) return;
    if (isEdit) {
      // Save changes first, then activate.
      updateMutation.mutate(undefined, {
        onSuccess: () => activateMutation.mutate(),
      });
    } else {
      createMutation.mutate(undefined, {
        onSuccess: (tmpl) => {
          // activateMutation uses templateId from state; update it first.
          void api.activateTemplate(tmpl.id).then(() => {
            invalidateList();
            void navigate({ to: "/templates" });
          });
        },
      });
    }
  }

  function updateField(index: number, updated: TemplateFieldIn) {
    setFields((prev) => prev.map((f, i) => (i === index ? updated : f)));
  }

  function removeField(index: number) {
    setFields((prev) => prev.filter((_, i) => i !== index));
  }

  function addBlankField() {
    setFields((prev) => [
      ...prev,
      { source_header: "", canonical_field: "", is_required: false },
    ]);
  }

  const availableCanonical = CANONICAL_FIELDS[type] ?? [];
  const isActive = existing?.status === "Active";

  if (isEdit && loadingExisting) {
    return <p className="text-sm text-text-2">Loading template…</p>;
  }

  return (
    <div className="max-w-3xl">
      {/* Header */}
      <div className="mb-6 flex items-center gap-3">
        <Link
          to="/templates"
          aria-label="Back to Template Library"
          className="flex items-center gap-1 text-xs text-text-2 hover:text-text transition-colors"
        >
          <IconArrowLeft size={14} aria-hidden />
          Templates
        </Link>
        <span className="text-text-3" aria-hidden>/</span>
        <h1 className="text-base font-semibold text-text">
          {isEdit ? "Edit template" : "New template"}
        </h1>
        {isActive && (
          <span className="ml-2 inline-flex items-center gap-1 rounded px-2 py-0.5 font-mono text-[11px] font-medium bg-status-match-bg text-status-match">
            <IconCheck size={10} aria-hidden />
            Active
          </span>
        )}
      </div>

      <div className="space-y-6">
        {/* Meta */}
        <section className="rounded-lg border border-border bg-bg-2 p-5">
          <h2 className="mb-4 text-sm font-medium text-text">Details</h2>
          <div className="grid grid-cols-2 gap-4">
            {/* Name */}
            <div className="col-span-2">
              <label htmlFor="tmpl-name" className="block text-xs font-medium text-text-2 mb-1">
                Name
              </label>
              <input
                id="tmpl-name"
                type="text"
                value={name}
                placeholder="e.g. FedEx Standard Q1 2026"
                className="w-full rounded border border-border bg-bg-3 px-3 py-2 text-sm text-text placeholder:text-text-3 focus:outline-none focus:ring-1 focus:ring-accent"
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            {/* Type */}
            <div>
              <label htmlFor="tmpl-type" className="block text-xs font-medium text-text-2 mb-1">
                Type
              </label>
              <select
                id="tmpl-type"
                value={type}
                disabled={isEdit}
                className="w-full rounded border border-border bg-bg-3 px-3 py-2 text-sm text-text focus:outline-none focus:ring-1 focus:ring-accent disabled:opacity-60 disabled:cursor-not-allowed"
                onChange={(e) => setType(e.target.value as TemplateType)}
              >
                {TEMPLATE_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
              {isEdit && (
                <p className="mt-0.5 text-[11px] text-text-3">
                  Template type cannot be changed after creation.
                </p>
              )}
            </div>

            {/* Surcharge format (hidden for manifest) */}
            {type !== "manifest" && (
              <div>
                <label
                  htmlFor="tmpl-surcharge-format"
                  className="block text-xs font-medium text-text-2 mb-1"
                >
                  Surcharge format
                </label>
                <select
                  id="tmpl-surcharge-format"
                  value={surchargeFormat}
                  className="w-full rounded border border-border bg-bg-3 px-3 py-2 text-sm text-text focus:outline-none focus:ring-1 focus:ring-accent"
                  onChange={(e) =>
                    setSurchargeFormat(e.target.value as SurchargeFormat | "")
                  }
                >
                  <option value="">Select format…</option>
                  {SURCHARGE_FORMATS.map((f) => (
                    <option key={f.value} value={f.value}>
                      {f.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Default flag */}
            <div className="col-span-2 flex items-center gap-2">
              <input
                id="tmpl-default"
                type="checkbox"
                checked={isDefault}
                className="h-4 w-4 rounded accent-accent cursor-pointer"
                onChange={(e) => setIsDefault(e.target.checked)}
              />
              <label htmlFor="tmpl-default" className="text-sm text-text-2 cursor-pointer">
                Use as default tiebreak template for this type
              </label>
            </div>
          </div>
        </section>

        {/* Header paste */}
        <section className="rounded-lg border border-border bg-bg-2 p-5">
          <h2 className="mb-1 text-sm font-medium text-text">Import headers</h2>
          <p className="mb-3 text-xs text-text-3">
            Paste the first row of your CSV file (comma or newline separated) to
            auto-populate the mapping grid below.
          </p>
          <div className="flex gap-2">
            <textarea
              value={headerPaste}
              aria-label="Paste CSV headers here"
              placeholder="tracking_number,shipment_date,billed_weight_kg,…"
              rows={2}
              className="flex-1 rounded border border-border bg-bg-3 px-3 py-2 font-mono text-xs text-text placeholder:text-text-3 focus:outline-none focus:ring-1 focus:ring-accent resize-none"
              onChange={(e) => setHeaderPaste(e.target.value)}
            />
            <button
              type="button"
              disabled={!headerPaste.trim() || isParsing}
              className="self-start rounded border border-border px-3 py-2 text-xs text-text-2 hover:text-text hover:bg-bg-3 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              onClick={parseHeaders}
            >
              Parse
            </button>
          </div>
        </section>

        {/* Mapping grid */}
        <section className="rounded-lg border border-border bg-bg-2 p-5">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-sm font-medium text-text">Field mappings</h2>
            <button
              type="button"
              className="flex items-center gap-1 text-xs text-text-2 hover:text-text transition-colors"
              onClick={addBlankField}
            >
              <IconPlus size={12} aria-hidden />
              Add row
            </button>
          </div>

          {fields.length === 0 ? (
            <p className="text-xs text-text-3">
              No mappings yet. Paste headers above or add rows manually.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-bg-3 text-left">
                    <th className="px-3 py-2 text-[11px] font-medium uppercase tracking-wide text-text-3">
                      Source header
                    </th>
                    <th className="px-3 py-2 text-[11px] font-medium uppercase tracking-wide text-text-3">
                      Canonical field
                    </th>
                    <th className="px-3 py-2 text-[11px] font-medium uppercase tracking-wide text-text-3 text-center">
                      Required
                    </th>
                    <th className="px-3 py-2" aria-label="Remove" />
                  </tr>
                </thead>
                <tbody>
                  {fields.map((f, i) => (
                    <MappingRow
                      key={i}
                      field={f}
                      index={i}
                      availableCanonical={availableCanonical}
                      onChange={updateField}
                      onRemove={removeField}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Errors */}
        {formError && (
          <div role="alert" className="alert error text-sm">
            {formError}
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-3 justify-end">
          <Link
            to="/templates"
            className="rounded border border-border px-4 py-2 text-sm text-text-2 hover:text-text hover:bg-bg-3 transition-colors"
          >
            Cancel
          </Link>
          <button
            type="button"
            disabled={isSaving}
            className="rounded border border-border px-4 py-2 text-sm text-text-2 hover:text-text hover:bg-bg-3 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            onClick={handleSaveDraft}
          >
            {isSaving ? "Saving…" : "Save as Draft"}
          </button>
          <button
            type="button"
            disabled={isSaving}
            className="flex items-center gap-2 rounded bg-accent px-4 py-2 text-sm font-medium text-bg hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
            onClick={handleActivate}
          >
            <IconCheck size={14} aria-hidden />
            {isSaving ? "Saving…" : "Activate"}
          </button>
        </div>
      </div>
    </div>
  );
}
